// SCR-STAFF-911 — فهرست، فیلتر، حذف گروهی و ثبت چندشعبه‌ای تعطیلات (US-PLT-095 تا 097).
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import Link from '@mui/material/Link';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { unwrap, type HolidayRowView, type TenantDayKind } from '@phoenix/api-client';
import { useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { BusinessDateField, DataTable, ErrorState, faDigits, ListPage, toBusinessDate, type DataTableColumn } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link as RouterLink } from 'react-router';
import { pltKeys } from './common';

export const SCR_STAFF_911 = 'SCR-STAFF-911';

const ORIGINS = ['', 'Public', 'Tenant', 'Branch'] as const;
const KINDS = ['', 'Closed', 'Open', 'CustomHours'] as const;
const LIMITS = [25, 50, 100] as const;

export function HolidayManagementPage() {
  const { t } = useTranslation('plt');
  const { tenantApi, session } = useTenantSession();
  const write = useWriteAccess('plt.settings.manage');
  const queryClient = useQueryClient();
  const [origin, setOrigin] = useState<(typeof ORIGINS)[number]>('');
  const [kind, setKind] = useState<(typeof KINDS)[number]>('');
  const [limit, setLimit] = useState<(typeof LIMITS)[number]>(25);
  const [selected, setSelected] = useState<string[]>([]);
  const [preview, setPreview] = useState<number | null>(null);
  const [adding, setAdding] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const tenantId = session?.tenantId;

  const holidays = useQuery({
    queryKey: [...pltKeys.holidays(tenantId), origin, kind, limit],
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/platform/calendar/holidays', {
          params: { query: { origin: origin || undefined, kind: kind || undefined, limit } },
        }),
      ),
  });
  const branches = useQuery({
    queryKey: pltKeys.branches(tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/platform/branches')),
  });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: pltKeys.holidays(tenantId) });
  const items = holidays.data?.items ?? [];

  const previewDelete = useMutation({
    mutationFn: (ids: string[]) => unwrap(tenantApi.POST('/api/v1/platform/calendar/holidays:bulk-delete-preview', { body: { ids } })),
    onSuccess: (result) => setPreview(Number(result.restorableCount)),
  });
  const remove = useMutation({
    mutationFn: (ids: string[]) => unwrap(tenantApi.POST('/api/v1/platform/calendar/holidays:bulk-delete', { body: { ids } })),
    onSuccess: () => {
      setSelected([]);
      setPreview(null);
      setNotice(t('plt:holidays.deleted'));
      refresh();
    },
  });

  const columns: DataTableColumn<HolidayRowView>[] = [
    {
      id: 'select',
      headerKey: 'plt:holidays.branches',
      cell: (row) =>
        write && !row.readOnly ? (
          <Checkbox
            size="small"
            checked={selected.includes(row.id)}
            onChange={() => setSelected((current) => (current.includes(row.id) ? current.filter((id) => id !== row.id) : [...current, row.id]))}
            inputProps={{ 'aria-label': row.title }}
          />
        ) : null,
    },
    { id: 'title', headerKey: 'plt:holidays.day_title', cell: (row) => row.title },
    { id: 'origin', headerKey: 'plt:holidays.origin', cell: (row) => t(`plt:holidays.origins.${row.origin ?? 'Tenant'}`) },
    { id: 'kind', headerKey: 'plt:holidays.kind', cell: (row) => t(`plt:holidays.kinds.${row.kind}`) },
    {
      id: 'flags',
      headerKey: 'plt:holidays.read_only',
      cell: (row) => (
        <Stack direction="row" spacing={1}>
          {row.readOnly ? <Chip size="small" label={t('plt:holidays.read_only')} /> : null}
          {row.isSample ? <Chip size="small" color="warning" label={t('plt:holidays.sample')} data-testid="holiday-sample" /> : null}
        </Stack>
      ),
    },
  ];

  return (
    <ListPage
      screenId={SCR_STAFF_911}
      titleKey="plt:holidays.board_title"
      subtitleKey="plt:holidays.board_subtitle"
      actions={
        write ? (
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" onClick={() => setAdding(true)} data-testid="holiday-multi">
              {t('plt:holidays.multi_branch')}
            </Button>
            <Button
              variant="contained"
              color="error"
              disabled={selected.length === 0 || previewDelete.isPending}
              onClick={() => previewDelete.mutate(selected)}
              data-testid="holiday-delete"
            >
              {t('plt:holidays.delete_selected', { count: faDigits(selected.length) })}
            </Button>
          </Stack>
        ) : undefined
      }
      filters={
        <Stack direction="row" spacing={2} alignItems="center">
          <TextField select size="small" label={t('plt:holidays.origin')} value={origin} onChange={(event) => setOrigin(event.target.value as (typeof ORIGINS)[number])} inputProps={{ 'data-testid': 'holiday-origin' }}>
            {ORIGINS.map((value) => (
              <MenuItem key={value || 'all'} value={value}>
                {t(`plt:holidays.origins.${value || 'all'}`)}
              </MenuItem>
            ))}
          </TextField>
          <TextField select size="small" label={t('plt:holidays.kind')} value={kind} onChange={(event) => setKind(event.target.value as (typeof KINDS)[number])}>
            {KINDS.map((value) => (
              <MenuItem key={value || 'all'} value={value}>
                {value ? t(`plt:holidays.kinds.${value}`) : t('plt:holidays.origins.all')}
              </MenuItem>
            ))}
          </TextField>
          <TextField select size="small" label={t('plt:holidays.page_size')} value={limit} onChange={(event) => setLimit(Number(event.target.value) as (typeof LIMITS)[number])}>
            {LIMITS.map((value) => (
              <MenuItem key={value} value={value}>
                {faDigits(value)}
              </MenuItem>
            ))}
          </TextField>
          <Link component={RouterLink} to="/bulk-closures" data-testid="holiday-provider-link">
            {t('plt:holidays.provider_link')}
          </Link>
        </Stack>
      }
    >
      {notice ? <Chip label={notice} color="success" data-testid="holiday-notice" onDelete={() => setNotice(null)} /> : null}
      <DataTable
        captionKey="plt:holidays.board_title"
        columns={columns}
        rows={items}
        getRowId={(row) => row.id}
        isLoading={holidays.isPending}
        error={holidays.error ?? previewDelete.error ?? remove.error}
        onRetry={() => void holidays.refetch()}
        emptyTitleKey="plt:holidays.empty"
      />
      <Dialog open={preview !== null} onClose={() => setPreview(null)}>
        <DialogTitle>{t('plt:holidays.remove_title')}</DialogTitle>
        <DialogContent data-testid="holiday-preview">{t('plt:holidays.delete_preview', { count: faDigits(preview ?? 0) })}</DialogContent>
        <DialogActions>
          <Button onClick={() => setPreview(null)}>{t('common:actions.cancel')}</Button>
          <Button color="error" disabled={remove.isPending} onClick={() => remove.mutate(selected)} data-testid="holiday-delete-confirm">
            {t('plt:holidays.remove')}
          </Button>
        </DialogActions>
      </Dialog>
      <MultiBranchDialog
        open={adding}
        branches={(branches.data?.items ?? []).map((branch) => ({ id: branch.id, name: branch.name }))}
        onClose={() => setAdding(false)}
        onSaved={() => {
          setAdding(false);
          setNotice(t('plt:holidays.multi_saved'));
          refresh();
        }}
      />
    </ListPage>
  );
}

function MultiBranchDialog({
  open,
  branches,
  onClose,
  onSaved,
}: {
  open: boolean;
  branches: { id: string; name: string }[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation('plt');
  const { tenantApi } = useTenantSession();
  const [picked, setPicked] = useState<string[]>([]);
  const [from, setFrom] = useState(() => toBusinessDate(new Date()) ?? '');
  const [title, setTitle] = useState('');
  const [kind, setKind] = useState<TenantDayKind>('Closed');
  const save = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/platform/calendar/holidays:multi-branch', {
          body: { branchIds: picked, from, to: from, kind, title, openFrom: kind === 'CustomHours' ? '08:00' : null, openTo: kind === 'CustomHours' ? '12:00' : null },
        }),
      ),
    onSuccess: onSaved,
  });
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('plt:holidays.multi_branch')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label={t('plt:holidays.day_title')} value={title} onChange={(event) => setTitle(event.target.value)} inputProps={{ 'data-testid': 'holiday-multi-title' }} />
          <BusinessDateField label={t('plt:holidays.from')} value={from} onChange={setFrom} testId="holiday-multi-from" />
          <TextField select label={t('plt:holidays.kind')} value={kind} onChange={(event) => setKind(event.target.value as TenantDayKind)}>
            {(['Closed', 'Open', 'CustomHours'] as const).map((value) => (
              <MenuItem key={value} value={value}>
                {t(`plt:holidays.kinds.${value}`)}
              </MenuItem>
            ))}
          </TextField>
          {branches.map((branch) => (
            <FormControlLabel
              key={branch.id}
              control={
                <Checkbox
                  checked={picked.includes(branch.id)}
                  onChange={() => setPicked((current) => (current.includes(branch.id) ? current.filter((id) => id !== branch.id) : [...current, branch.id]))}
                />
              }
              label={branch.name}
            />
          ))}
          {save.error ? <ErrorState error={save.error} compact /> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common:actions.cancel')}</Button>
        <Button variant="contained" disabled={picked.length === 0 || title.trim() === '' || save.isPending} onClick={() => save.mutate()} data-testid="holiday-multi-save">
          {t('common:actions.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
